import { Injectable, inject } from '@angular/core';
import { LocalizationService, LocalizationParam } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ConfirmDialogState } from 'src/core/models/confirmation/confirm-dialog-state.model';
import { ConfirmSeverity } from 'src/core/models/confirmation/confirm-severity.model';

@Injectable({
  providedIn: 'root',
})
export class ConfirmationService {
  private readonly localization = inject(LocalizationService);

  readonly dialogState$ = new Subject<ConfirmDialogState | null>();

  warn(
    message: LocalizationParam,
    title?: LocalizationParam,
    options?: Partial<Confirmation.Options>,
  ): Observable<Confirmation.Status> {
    return this.show(message, title, 'warn', options);
  }

  info(
    message: LocalizationParam,
    title?: LocalizationParam,
    options?: Partial<Confirmation.Options>,
  ): Observable<Confirmation.Status> {
    return this.show(message, title, 'info', options);
  }

  error(
    message: LocalizationParam,
    title?: LocalizationParam,
    options?: Partial<Confirmation.Options>,
  ): Observable<Confirmation.Status> {
    return this.show(message, title, 'error', options);
  }

  success(
    message: LocalizationParam,
    title?: LocalizationParam,
    options?: Partial<Confirmation.Options>,
  ): Observable<Confirmation.Status> {
    return this.show(message, title, 'success', options);
  }

  private show(
    message: LocalizationParam,
    title: LocalizationParam,
    severity: ConfirmSeverity,
    options?: Partial<Confirmation.Options>,
  ): Observable<Confirmation.Status> {
    const answer$ = new Subject<Confirmation.Status>();

    const state: ConfirmDialogState = {
      title: title ? this.localization.instant(title) : '',
      message: this.localization.instant(message, ...(options?.messageLocalizationParams ?? [])),
      severity,
      yesLabel: options?.yesText
        ? this.localization.instant(options.yesText)
        : this.localization.instant('AbpUi::Yes'),
      cancelLabel: options?.cancelText
        ? this.localization.instant(options.cancelText)
        : this.localization.instant('AbpUi::Cancel'),
      dismissible: options?.dismissible ?? true,
      acceptVisible: !(options?.hideYesBtn ?? false),
      rejectVisible: !(options?.hideCancelBtn ?? false),
      answer$,
    };

    this.dialogState$.next(state);

    return answer$.asObservable().pipe(take(1));
  }
}
